import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth, useTheme, api } from "../App";
import AppShell from "../components/AppShell";
import { useSubscription } from "../hooks/useSubscription";
import {
  Check,
  LogOut,
  Zap,
  CreditCard,
  Brain,
  Moon,
  Sun,
  Cloud,
  GitBranch,
  Shield,
  Code,
  Smartphone,
  BarChart,
  Link,
  Settings,
  Layout,
  Palette,
  Rocket,
  Users,
  TrendingUp,
  Github,
  Terminal,
  Save,
  Linkedin,
  ExternalLink,
  List,
  Flame,
  BookOpen,
  Lightbulb,
  FlaskConical,
  Upload,
  FileText,
} from "lucide-react";

const SENIORITY_OPTIONS = [
  { value: "software-engineer", label: "Software Engineer" },
  { value: "senior-engineer", label: "Senior Engineer" },
  { value: "staff-engineer", label: "Staff / Principal" },
  { value: "tech-lead", label: "Tech Lead" },
  { value: "engineering-manager", label: "Eng Manager" },
  { value: "director", label: "Director" },
  { value: "vp", label: "VP of Eng" },
  { value: "cto", label: "CTO" },
];

const POST_STYLE_OPTIONS = [
  { value: "technical", label: "Technical Depth", description: "Code examples, architectural breakdowns", icon: Code },
  { value: "story", label: "Personal Story", description: "Relatable narratives, lessons learned", icon: BookOpen },
  { value: "leadership", label: "Leadership Angle", description: "Strategic insights, team dynamics", icon: TrendingUp },
  { value: "listicle", label: "Listicle / Tips", description: "Numbered takeaways, actionable checklists", icon: List },
  { value: "contrarian", label: "Contrarian Take", description: "Hot takes, challenging conventional wisdom", icon: Flame },
  { value: "case-study", label: "Case Study", description: "Problem → approach → measurable results", icon: FlaskConical },
  { value: "insight", label: "Industry Insight", description: "Trend analysis, forward-looking takes", icon: Lightbulb },
];

const TECH_KEYWORD_OPTIONS = [
  "Python", "JavaScript", "TypeScript", "Go", "Rust", "Java", "C++", "Swift", "Kotlin", "SQL",
  "React", "Vue", "Angular", "Next.js", "Tailwind CSS",
  "Node.js", "Django", "FastAPI", "Spring Boot", "Rails",
  "AWS", "Azure", "GCP", "Serverless",
  "LLMs", "PyTorch", "TensorFlow", "LangChain", "OpenAI API",
  "Docker", "Kubernetes", "Terraform", "CI/CD", "GitHub Actions",
  "PostgreSQL", "MongoDB", "Redis", "DynamoDB",
  "GraphQL", "REST APIs", "Microservices", "System Design",
];

const TOPIC_ICONS = {
  "ai-ml": Brain, "cloud": Cloud, "devops": GitBranch, "security": Shield,
  "web-dev": Code, "mobile": Smartphone, "data-science": BarChart,
  "blockchain": Link, "engineering": Settings, "product": Layout,
  "ux-design": Palette, "startup": Rocket, "leadership": Users,
  "career": TrendingUp, "open-source": Github, "programming": Terminal,
};

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "topics", label: "Topics" },
  { id: "preferences", label: "Preferences" },
  { id: "billing", label: "Billing" },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isPro, isUltra, isTrialActive, trialDaysRemaining, postsUsedToday, subscription, refetch } = useSubscription();
  const { theme, toggleTheme } = useTheme();
  const { logout } = useAuth();

  const [activeTab, setActiveTab] = useState("profile");
  const [isLoading, setIsLoading] = useState(true);

  // Dirty tracking — single Save button per tab
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Profile tab
  const [linkedinProfile, setLinkedinProfile] = useState("");
  const [originalLinkedinProfile, setOriginalLinkedinProfile] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [isPdfUploading, setIsPdfUploading] = useState(false);
  const [pdfUploadResult, setPdfUploadResult] = useState(null);

  // Topics tab
  const [availableTopics, setAvailableTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [originalTopics, setOriginalTopics] = useState([]);

  // Preferences tab
  const [seniority, setSeniority] = useState("");
  const [originalSeniority, setOriginalSeniority] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [originalKeywords, setOriginalKeywords] = useState([]);
  const [preferredStyles, setPreferredStyles] = useState([]);
  const [originalPreferredStyles, setOriginalPreferredStyles] = useState([]);
  const [personalExperiences, setPersonalExperiences] = useState([]);
  const [originalPersonalExperiences, setOriginalPersonalExperiences] = useState([]);
  const [newExperienceText, setNewExperienceText] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [topicsRes, userTopicsRes, userRes, selfProfileRes] = await Promise.all([
        api.get("/topics"),
        api.get("/user/topics"),
        api.get("/auth/me"),
        api.get("/user/self-profile"),
      ]);

      setAvailableTopics(topicsRes.data.topics);
      setSelectedTopics(userTopicsRes.data.topics);
      setOriginalTopics(userTopicsRes.data.topics);
      setLinkedinProfile(userRes.data.linkedin_profile || "");
      setOriginalLinkedinProfile(userRes.data.linkedin_profile || "");

      const sp = selfProfileRes.data;
      setSeniority(sp.seniority || "");
      setOriginalSeniority(sp.seniority || "");
      setSelectedKeywords(sp.tech_keywords || []);
      setOriginalKeywords(sp.tech_keywords || []);
      setPreferredStyles(sp.preferred_post_style || []);
      setOriginalPreferredStyles(sp.preferred_post_style || []);
      setPersonalExperiences(sp.personal_experiences || []);
      setOriginalPersonalExperiences(sp.personal_experiences || []);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset dirty state when switching tabs
  useEffect(() => { setIsDirty(false); }, [activeTab]);

  // Poll for subscription upgrade
  useEffect(() => {
    if (searchParams.get("billing") !== "success") return;
    navigate("/settings", { replace: true });
    const poll = async (attempts) => {
      await refetch();
      if (attempts > 1) setTimeout(() => poll(attempts - 1), 2000);
    };
    toast.success("Subscription activated! Welcome to Pro.");
    poll(3);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save handlers per tab ──
  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (activeTab === "profile") {
        await api.put("/user/profile", { linkedin_profile: linkedinProfile });
        setOriginalLinkedinProfile(linkedinProfile);
        toast.success("Profile saved");
      } else if (activeTab === "topics") {
        await api.post("/user/topics", { topics: selectedTopics });
        setOriginalTopics(selectedTopics);
        toast.success("Topics saved");
      } else if (activeTab === "preferences") {
        await api.post("/user/self-profile", {
          seniority: seniority || null,
          tech_keywords: selectedKeywords,
          preferred_post_style: preferredStyles,
          personal_experiences: personalExperiences,
        });
        setOriginalSeniority(seniority);
        setOriginalKeywords([...selectedKeywords]);
        setOriginalPreferredStyles([...preferredStyles]);
        setOriginalPersonalExperiences([...personalExperiences]);
        toast.success("Preferences saved");
      }
      setIsDirty(false);
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) return;
    setIsPdfUploading(true);
    setPdfUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", pdfFile);
      const res = await api.post("/user/linkedin-pdf", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfUploadResult(res.data);
      setPdfFile(null);
      toast.success("LinkedIn profile imported!");
      const sp = await api.get("/user/self-profile");
      setSelectedKeywords(sp.data.tech_keywords || []);
      setOriginalKeywords(sp.data.tech_keywords || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to parse PDF");
    } finally {
      setIsPdfUploading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
  };

  const getIcon = (iconName) => {
    const IconComponent = TOPIC_ICONS[iconName] || Code;
    return <IconComponent className="w-4 h-4" />;
  };

  const markDirty = () => setIsDirty(true);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading settings…</div>
      </div>
    );
  }

  return (
    <AppShell activeNav="settings">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Sticky tabbed header ── */}
        <div className="sticky top-0 z-10 bg-background border-b border-border -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between pt-5 pb-0">
            <h1 className="font-headline font-extrabold text-xl text-foreground">Settings</h1>
            {/* Single save button — appears only when dirty */}
            {isDirty && activeTab !== "billing" && (
              <Button
                onClick={handleSave}
                disabled={isSaving}
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-1.5"
              >
                {isSaving ? (
                  "Saving…"
                ) : (
                  <><Save className="w-3.5 h-3.5" /> Save Changes</>
                )}
              </Button>
            )}
          </div>
          {/* Tab bar */}
          <div className="flex gap-0 mt-3">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab content ── */}
        <div className="py-6 flex flex-col gap-4">

          {/* ── PROFILE TAB ── */}
          {activeTab === "profile" && (
            <>
              {/* User info card */}
              <div className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full shrink-0" />
                ) : (
                  <div className="w-14 h-14 bg-foreground rounded-full flex items-center justify-center text-background text-xl font-bold shrink-0">
                    {user?.name?.charAt(0) || "U"}
                  </div>
                )}
                <div>
                  <p className="font-headline font-bold text-foreground">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>

              {/* LinkedIn URL */}
              <div className="p-4 bg-card rounded-xl border border-border space-y-3">
                <div className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                  <Label htmlFor="linkedin-url" className="font-semibold text-sm">LinkedIn Profile URL</Label>
                </div>
                <div className="flex gap-2">
                  <Input
                    id="linkedin-url"
                    type="url"
                    placeholder="https://linkedin.com/in/yourprofile"
                    value={linkedinProfile}
                    onChange={(e) => { setLinkedinProfile(e.target.value); markDirty(); }}
                    data-testid="linkedin-profile-input"
                    className="flex-1 bg-background"
                  />
                  {linkedinProfile && (
                    <Button variant="outline" size="icon" asChild>
                      <a href={linkedinProfile} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>

              {/* LinkedIn PDF import */}
              <div className="p-4 bg-card rounded-xl border border-border space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#0A66C2]" />
                  <Label className="font-semibold text-sm">Import Profile from PDF</Label>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  On LinkedIn: Your Profile → <span className="font-medium text-foreground">More</span> → <span className="font-medium text-foreground">Save to PDF</span>. We'll extract your experience and tech keywords automatically.
                </p>
                <div className="flex gap-2 items-center">
                  <label className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background hover:border-primary/50 cursor-pointer transition-colors text-sm text-muted-foreground">
                    <Upload className="w-4 h-4 shrink-0" />
                    <span className="truncate">{pdfFile ? pdfFile.name : "Choose LinkedIn PDF…"}</span>
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => { setPdfFile(e.target.files[0] || null); setPdfUploadResult(null); }}
                    />
                  </label>
                  <Button
                    size="sm"
                    onClick={handlePdfUpload}
                    disabled={!pdfFile || isPdfUploading}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground shrink-0"
                  >
                    {isPdfUploading ? "Importing…" : "Import"}
                  </Button>
                </div>
                {pdfUploadResult && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-emerald-800 dark:text-emerald-300">{pdfUploadResult.name}</p>
                      {pdfUploadResult.headline && (
                        <p className="text-emerald-700 dark:text-emerald-400 text-xs mt-0.5">{pdfUploadResult.headline}</p>
                      )}
                      {pdfUploadResult.tech_keywords?.length > 0 && (
                        <p className="text-emerald-600 dark:text-emerald-500 text-xs mt-1">
                          {pdfUploadResult.tech_keywords.length} tech keywords extracted
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Danger zone */}
              <div className="p-4 bg-card rounded-xl border border-destructive/20">
                <p className="text-sm font-semibold text-foreground mb-3">Account</p>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="w-full text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </Button>
              </div>
            </>
          )}

          {/* ── TOPICS TAB ── */}
          {activeTab === "topics" && (
            <>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-muted-foreground">Select 1–7 topics for your feed.</p>
                <div className="px-2.5 py-1 bg-muted rounded-full text-xs">
                  <span className="font-semibold text-foreground">{selectedTopics.length}</span>
                  <span className="text-muted-foreground"> / 7</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {availableTopics.map((topic) => {
                  const isSelected = selectedTopics.includes(topic.id);
                  return (
                    <button
                      key={topic.id}
                      onClick={() => {
                        setSelectedTopics((prev) => {
                          if (prev.includes(topic.id)) {
                            if (prev.length <= 1) { toast.error("Select at least 1 topic"); return prev; }
                            return prev.filter((t) => t !== topic.id);
                          }
                          if (prev.length >= 7) { toast.error("Maximum 7 topics"); return prev; }
                          return [...prev, topic.id];
                        });
                        markDirty();
                      }}
                      data-testid={`topic-${topic.id}`}
                      className={`relative p-3.5 rounded-xl border-2 text-left transition-all duration-150 hover:-translate-y-0.5 ${
                        isSelected
                          ? "border-primary bg-primary/8 dark:bg-primary/12"
                          : "border-border bg-card hover:border-primary/40"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                        }`}>
                          {getIcon(topic.id)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-foreground truncate">{topic.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{topic.description}</p>
                        </div>
                        {isSelected && (
                          <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center shrink-0">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ── PREFERENCES TAB ── */}
          {activeTab === "preferences" && (
            <>
              {/* Appearance */}
              <div className="p-4 bg-card rounded-xl border border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {theme === "dark"
                      ? <Moon className="w-4 h-4 text-muted-foreground" />
                      : <Sun className="w-4 h-4 text-muted-foreground" />
                    }
                    <div>
                      <Label className="font-semibold text-sm text-foreground">Dark Mode</Label>
                      <p className="text-xs text-muted-foreground">Toggle light/dark appearance</p>
                    </div>
                  </div>
                  <Switch
                    checked={theme === "dark"}
                    onCheckedChange={toggleTheme}
                    data-testid="dark-mode-toggle"
                  />
                </div>
              </div>

              {/* Role */}
              <div className="p-4 bg-card rounded-xl border border-border space-y-3">
                <Label className="font-semibold text-sm">Your Role</Label>
                <div className="flex flex-wrap gap-2">
                  {SENIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setSeniority(seniority === opt.value ? "" : opt.value); markDirty(); }}
                      className={`px-3 py-1.5 rounded-full text-xs border-2 transition-all duration-150 ${
                        seniority === opt.value
                          ? "border-primary bg-primary/10 text-primary font-semibold"
                          : "border-border bg-background hover:border-primary/40 text-foreground font-medium"
                      }`}
                    >
                      {seniority === opt.value && <Check className="w-2.5 h-2.5 inline mr-1" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Post style */}
              <div className="p-4 bg-card rounded-xl border border-border space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold text-sm">Preferred Post Style</Label>
                  <span className="text-xs text-muted-foreground">
                    <span className={preferredStyles.length === 3 ? "text-primary font-semibold" : ""}>{preferredStyles.length}</span>/3
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {POST_STYLE_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const isSelected = preferredStyles.includes(opt.value);
                    const isDisabled = !isSelected && preferredStyles.length >= 3;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setPreferredStyles((prev) => {
                            if (prev.includes(opt.value)) return prev.filter((s) => s !== opt.value);
                            if (prev.length >= 3) { toast.error("Max 3 styles"); return prev; }
                            return [...prev, opt.value];
                          });
                          markDirty();
                        }}
                        disabled={isDisabled}
                        className={`p-3 rounded-xl border-2 text-left transition-all duration-150 ${
                          isDisabled ? "opacity-40 cursor-not-allowed" : ""
                        } ${
                          isSelected
                            ? "border-primary bg-primary/8 dark:bg-primary/12"
                            : "border-border bg-background hover:border-primary/40"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <Icon className={`w-3.5 h-3.5 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                          <span className={`text-xs font-semibold ${isSelected ? "text-primary" : "text-foreground"}`}>{opt.label}</span>
                          {isSelected && <Check className="w-3 h-3 text-primary ml-auto shrink-0" />}
                        </div>
                        <p className="text-[11px] text-muted-foreground">{opt.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tech keywords */}
              <div className="p-4 bg-card rounded-xl border border-border space-y-3">
                <div>
                  <Label className="font-semibold text-sm">Tech Stack</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Selected technologies are woven into your posts.</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {TECH_KEYWORD_OPTIONS.map((kw) => {
                    const isSelected = selectedKeywords.includes(kw);
                    return (
                      <button
                        key={kw}
                        onClick={() => {
                          setSelectedKeywords((prev) =>
                            prev.includes(kw) ? prev.filter((k) => k !== kw) : [...prev, kw]
                          );
                          markDirty();
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-all duration-150 ${
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground font-medium"
                            : "border-border bg-background text-foreground hover:border-primary/50"
                        }`}
                      >
                        {kw}
                      </button>
                    );
                  })}
                </div>
                {selectedKeywords.length > 0 && (
                  <p className="text-xs text-muted-foreground">{selectedKeywords.length} selected</p>
                )}
              </div>

              {/* Personal Experiences */}
              <div className="p-4 bg-card rounded-xl border border-border space-y-3">
                <div>
                  <Label className="font-semibold text-sm">My Experiences</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Real moments from your career — failures, wins, decisions, tools you've used. Posts will draw from these for authentic, specific details instead of invented ones.
                  </p>
                </div>

                {/* Existing experiences */}
                {personalExperiences.length > 0 && (
                  <ul className="space-y-1.5">
                    {personalExperiences.map((exp, idx) => (
                      <li key={idx} className="flex items-start gap-2 group">
                        <span className="text-xs text-muted-foreground mt-1 shrink-0">•</span>
                        <span className="text-sm text-foreground flex-1 leading-snug">{exp}</span>
                        <button
                          onClick={() => {
                            setPersonalExperiences((prev) => prev.filter((_, i) => i !== idx));
                            markDirty();
                          }}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0 mt-0.5"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Add new experience */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newExperienceText}
                    onChange={(e) => setNewExperienceText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newExperienceText.trim() && personalExperiences.length < 10) {
                        setPersonalExperiences((prev) => [...prev, newExperienceText.trim()]);
                        setNewExperienceText("");
                        markDirty();
                      }
                    }}
                    placeholder="e.g. Migrated 200GB Postgres DB to Aurora with zero downtime"
                    disabled={personalExperiences.length >= 10}
                    className="flex-1 text-sm px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground/60 disabled:opacity-50"
                  />
                  <button
                    disabled={personalExperiences.length >= 10}
                    onClick={() => {
                      if (newExperienceText.trim() && personalExperiences.length < 10) {
                        setPersonalExperiences((prev) => [...prev, newExperienceText.trim()]);
                        setNewExperienceText("");
                        markDirty();
                      }
                    }}
                    className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    Add
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {personalExperiences.length}/10 · Press Enter or click Add
                </p>
              </div>
            </>
          )}

          {/* ── BILLING TAB ── */}
          {activeTab === "billing" && (
            <>
              {/* Current plan card */}
              {isPro ? (
                <div className="p-5 bg-primary/8 dark:bg-primary/12 rounded-xl border border-primary/20">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1.5 bg-primary/15 text-primary text-xs font-bold px-2.5 py-1 rounded-full">
                      <Zap className="w-3 h-3" /> {isUltra ? "ULTRA" : "PRO"}
                    </span>
                    <span className="text-sm text-muted-foreground">Active</span>
                  </div>
                  {subscription?.subscription_period_end && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Renews{" "}
                      {new Date(subscription.subscription_period_end).toLocaleDateString(undefined, {
                        year: "numeric", month: "long", day: "numeric",
                      })}
                    </p>
                  )}
                  <Button variant="outline" size="sm" onClick={() => navigate("/pricing")} className="mt-3">
                    Manage Subscription
                  </Button>
                </div>
              ) : subscription?.subscription_status === "past_due" ? (
                <div className="p-4 bg-destructive/8 rounded-xl border border-destructive/20 space-y-3">
                  <p className="text-sm font-semibold text-destructive">Payment failed</p>
                  <p className="text-sm text-muted-foreground">Update your payment method to keep Pro access.</p>
                  <Button size="sm" onClick={() => navigate("/pricing")}>Update Payment</Button>
                </div>
              ) : subscription?.subscription_status === "cancelled" ? (
                <div className="p-4 bg-card rounded-xl border border-border space-y-3">
                  <p className="text-sm text-muted-foreground">Your subscription has been cancelled.</p>
                  <Button size="sm" onClick={() => navigate("/pricing")}>
                    <Zap className="w-3.5 h-3.5 mr-1.5" /> Resubscribe
                  </Button>
                </div>
              ) : isTrialActive ? (
                <div className="p-5 bg-primary/8 dark:bg-primary/12 rounded-xl border border-primary/20">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs font-bold text-primary uppercase tracking-wide">Free Trial</p>
                      <p className="font-headline font-extrabold text-2xl text-foreground mt-0.5">
                        {trialDaysRemaining} day{trialDaysRemaining !== 1 ? "s" : ""} left
                      </p>
                    </div>
                    {postsUsedToday !== null && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Posts today</p>
                        <p className="font-headline font-extrabold text-2xl text-foreground">
                          {postsUsedToday}<span className="text-sm font-normal text-muted-foreground">/5</span>
                        </p>
                      </div>
                    )}
                  </div>
                  {postsUsedToday !== null && (
                    <div className="flex gap-1 mb-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className={`h-1.5 flex-1 rounded-full ${i < postsUsedToday ? "bg-primary" : "bg-border"}`} />
                      ))}
                    </div>
                  )}
                  <Button
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-headline font-bold"
                    onClick={() => navigate("/pricing")}
                  >
                    <Zap className="w-4 h-4 mr-2" /> Upgrade to Pro
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">Cancel anytime · No commitment</p>
                </div>
              ) : (
                <div className="p-4 bg-destructive/8 rounded-xl border border-destructive/20 space-y-3">
                  <p className="text-sm font-semibold text-destructive">Your free trial has ended</p>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => navigate("/pricing")}>
                    <Zap className="w-4 h-4 mr-2" /> Subscribe to continue
                  </Button>
                </div>
              )}

              {/* Plan comparison */}
              <div className="p-4 bg-card rounded-xl border border-border">
                <p className="text-sm font-semibold text-foreground mb-4">Available Plans</p>
                <div className="space-y-3">
                  {[
                    { name: "Free Trial", price: "$0", desc: "5 posts/day · All frameworks · 7 days" },
                    { name: "Pro", price: "$19.99/mo", desc: "Unlimited posts · Carousel creation · All frameworks" },
                    { name: "Ultra", price: "$34.99/mo", desc: "Everything in Pro + AI image generation" },
                  ].map((plan, i) => (
                    <div key={i} className={`flex items-center justify-between py-2.5 ${i < 2 ? "border-b border-border" : ""}`}>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{plan.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{plan.desc}</p>
                      </div>
                      <p className={`text-sm font-bold shrink-0 ml-4 ${i === 0 ? "text-muted-foreground" : "text-primary"}`}>
                        {plan.price}
                      </p>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-4"
                  onClick={() => navigate("/pricing")}
                >
                  View full pricing
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default SettingsPage;
